import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PiChatCircleDotsDuotone, PiMoonDuotone, PiSunDuotone, PiTelevisionSimpleBold } from 'react-icons/pi';
import { ActionIcon, cn, useTheme } from '@v2/design-system';
import { siteConfig } from '@v2/config/site';
import { ADMIN_ROLES, hasAnyRole } from '@v2/lib/auth';
import { useI18n } from '@v2/providers/I18nProvider';
import HeaderLanguageSwitcher from './HeaderLanguageSwitcher';
import HeaderNotifications from './HeaderNotifications';
import HeaderUsdRates from './HeaderUsdRates';
import ApproveTvModal from '@v2/features/finance-tv/ApproveTvModal';

export default function Header() {
    const { t } = useI18n();
    const user = window.__V2__?.user;
    const logoutUrl = window.__V2__?.logoutUrl ?? '/admin/v1/logout';
    const canManageUsers = hasAnyRole([...ADMIN_ROLES]);
    const { theme, toggleTheme } = useTheme();
    const [open, setOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const [tvModalOpen, setTvModalOpen] = useState(false);

    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
    const isDark = theme === 'dark';
    const logo = isDark ? siteConfig.logoDark : siteConfig.logo;

    return (
        <>
        <ApproveTvModal open={tvModalOpen} onClose={() => setTvModalOpen(false)} />
        <header
            className={cn(
                'sticky top-0 z-[60] flex items-center justify-between border-b px-4 py-3 backdrop-blur md:px-5 lg:px-6',
                isDark ? 'border-gray-700/60 bg-gray-900/95' : 'border-gray-200 bg-white/90',
            )}
        >
            {/* Logo mobile (visível só quando sidebar está oculta) */}
            <div className="xl:hidden">
                <img src={logo} alt={siteConfig.name} className="h-7" />
            </div>

            {/* Espaço central */}
            <div className="flex-1" />

            {/* Ações do lado direito */}
            <div className="flex items-center gap-2">
                <HeaderUsdRates />
                <HeaderLanguageSwitcher />

                {/* Notificações */}
                <HeaderNotifications />

                {/* Mensagens — atalho para comentários/tarefas */}
                <Link
                    to="/shipments"
                    aria-label={t('v2.header.messages')}
                    className={cn(
                        'relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
                        isDark
                            ? 'border-gray-700 bg-gray-800 hover:bg-gray-700'
                            : 'border-gray-200 bg-white hover:bg-gray-50',
                    )}
                >
                    <PiChatCircleDotsDuotone className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </Link>

                {/* Parear TV */}
                <button
                    type="button"
                    onClick={() => setTvModalOpen(true)}
                    aria-label="Parear TV"
                    title="Parear TV — Painel Financeiro"
                    className={cn(
                        'relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
                        isDark
                            ? 'border-gray-700 bg-gray-800 hover:bg-gray-700'
                            : 'border-gray-200 bg-white hover:bg-gray-50',
                    )}
                >
                    <PiTelevisionSimpleBold className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </button>

                {/* Toggle tema */}
                <ActionIcon
                    variant="outline"
                    onClick={toggleTheme}
                    aria-label={t('v2.header.toggle_theme')}
                    className={cn(
                        isDark
                            ? 'border-gray-700 bg-gray-800 hover:bg-gray-700'
                            : 'border-gray-200 bg-white hover:bg-gray-50',
                    )}
                >
                    {theme === 'light'
                        ? <PiMoonDuotone className="h-5 w-5 text-gray-600" />
                        : <PiSunDuotone className="h-5 w-5 text-gray-300" />
                    }
                </ActionIcon>

                {/* Menu do usuário */}
                <div className="relative" ref={menuRef}>
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className={cn(
                            'flex items-center gap-2 rounded-full py-1 pl-2 pr-1',
                            isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100',
                        )}
                    >
                        <span className={cn('hidden text-sm sm:inline', isDark ? 'text-gray-300' : 'text-gray-600')}>
                            {user?.name}
                        </span>
                        {user?.avatar_url ? (
                            <img
                                src={user.avatar_url}
                                alt={user.name}
                                className="size-9 rounded-full border border-gray-100 object-cover"
                            />
                        ) : (
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-lighter text-sm font-semibold text-primary-dark">
                                {user?.name?.charAt(0) ?? '?'}
                            </span>
                        )}
                    </button>

                    {open && (
                        <div
                            className={cn(
                                'absolute right-0 top-full mt-2 w-56 rounded-lg border py-1 shadow-lg',
                                isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white',
                            )}
                        >
                            <div className={cn('border-b px-3 py-2', isDark ? 'border-gray-700' : 'border-gray-100')}>
                                <p className={cn('truncate text-sm font-medium', isDark ? 'text-gray-100' : 'text-gray-800')}>
                                    {user?.name}
                                </p>
                                <p className={cn('truncate text-xs', isDark ? 'text-gray-400' : 'text-gray-500')}>
                                    {user?.email}
                                </p>
                            </div>

                            <Link
                                to="/profile"
                                onClick={() => setOpen(false)}
                                className={cn(
                                    'block px-3 py-2 text-sm transition-colors',
                                    isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50',
                                )}
                            >
                                {t('v2.header.my_profile')}
                            </Link>
                            {canManageUsers && (
                                <Link
                                    to="/users"
                                    onClick={() => setOpen(false)}
                                    className={cn(
                                        'block px-3 py-2 text-sm transition-colors',
                                        isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50',
                                    )}
                                >
                                    {t('v2.header.manage_users')}
                                </Link>
                            )}
                            <form
                                method="POST"
                                action={logoutUrl}
                                className={cn('border-t', isDark ? 'border-gray-700' : 'border-gray-100')}
                            >
                                <input type="hidden" name="_token" value={csrf} />
                                <button
                                    type="submit"
                                    className={cn(
                                        'block w-full px-3 py-2 text-left text-sm text-red-500 transition-colors',
                                        isDark ? 'hover:bg-red-950/30' : 'hover:bg-red-50',
                                    )}
                                >
                                    {t('v2.header.logout')}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </header>
        </>
    );
}
